import { gql } from '@apollo/client'

const CUSTOMER_BOOKING_FIELDS = gql`
  fragment CustomerBookingFields on Booking {
    id
    status
    scheduledDate
    startTime
    endTime
    durationMinutes
    priceCents
    currency
    paymentMethod
    paymentStatus
    customerNotes
    providerNotes
    cancellationReason
    confirmedAt
    startedAt
    completedAt
    cancelledAt
    cancelledBy
    review {
      id
      rating
      comment
      createdAt
    }
    providerProfile {
      id
      user {
        firstName
        lastName
        avatarUrl
      }
    }
    serviceType {
      id
      name
      nameEl
      translationKey
    }
    property {
      id
      label
      propertyType
      addressLine1
      city
      postalCode
      floor
      hasElevator
      sizeSqm
      bedrooms
      bathrooms
    }
  }
`

const CUSTOMER_PROPERTY_FIELDS = gql`
  fragment CustomerPropertyFields on Property {
    id
    label
    propertyType
    addressLine1
    addressLine2
    city
    postalCode
    region
    country
    floor
    hasElevator
    sizeSqm
    bedrooms
    bathrooms
    accessInstructions
    notes
    isDefault
    preferences {
      id
      specialInstructions
      hasPets
      petDetails
      suppliesProvided
      serviceType {
        id
        name
        nameEl
        translationKey
      }
    }
  }
`

export const MY_BOOKINGS_QUERY = gql`
  ${CUSTOMER_BOOKING_FIELDS}

  query MyBookingsForCustomer($status: String, $upcoming: Boolean, $first: Int, $after: String) {
    myBookings(status: $status, upcoming: $upcoming, first: $first, after: $after) {
      totalCount
      nodes {
        ...CustomerBookingFields
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

export const MY_PROPERTIES_QUERY = gql`
  ${CUSTOMER_PROPERTY_FIELDS}

  query MyPropertiesForCustomer {
    myProperties {
      ...CustomerPropertyFields
    }
  }
`

export const CREATE_PROPERTY_MUTATION = gql`
  ${CUSTOMER_PROPERTY_FIELDS}

  mutation CreatePropertyFromPropertiesPage($input: CreatePropertyInput!) {
    createProperty(input: $input) {
      property {
        ...CustomerPropertyFields
      }
      errors
    }
  }
`

export const UPDATE_PROPERTY_MUTATION = gql`
  ${CUSTOMER_PROPERTY_FIELDS}

  mutation UpdatePropertyFromPropertiesPage($input: UpdatePropertyInput!) {
    updateProperty(input: $input) {
      property {
        ...CustomerPropertyFields
      }
      errors
    }
  }
`

export const DELETE_PROPERTY_MUTATION = gql`
  mutation DeletePropertyFromPropertiesPage($id: ID!) {
    deleteProperty(input: { id: $id }) {
      success
      errors
    }
  }
`

export const CANCEL_BOOKING_MUTATION = gql`
  ${CUSTOMER_BOOKING_FIELDS}

  mutation CancelCustomerBooking($bookingId: ID!, $reason: String) {
    cancelBooking(input: { bookingId: $bookingId, reason: $reason }) {
      booking {
        ...CustomerBookingFields
      }
      errors
    }
  }
`

export interface CustomerBookingProviderUser {
  firstName: string
  lastName: string
  avatarUrl: string | null
}

export interface CustomerBookingProviderProfile {
  id: string
  user: CustomerBookingProviderUser
}

export interface CustomerBookingServiceType {
  id: string
  name: string
  nameEl: string
  translationKey: string
}

export interface CustomerBookingProperty {
  id: string
  label: string
  propertyType: string
  addressLine1: string
  city: string
  postalCode: string
  floor: number | null
  hasElevator: boolean
  sizeSqm: number | null
  bedrooms: number | null
  bathrooms: number | null
}

export interface CustomerBooking {
  id: string
  status: string
  scheduledDate: string
  startTime: string
  endTime: string
  durationMinutes: number
  priceCents: number
  currency: string
  paymentMethod: string | null
  paymentStatus: string
  customerNotes: string | null
  providerNotes: string | null
  cancellationReason: string | null
  confirmedAt: string | null
  startedAt: string | null
  completedAt: string | null
  cancelledAt: string | null
  cancelledBy: string | null
  review: {
    id: string
    rating: number
    comment: string | null
    createdAt: string
  } | null
  providerProfile: CustomerBookingProviderProfile
  serviceType: CustomerBookingServiceType
  property: CustomerBookingProperty | null
}

export interface CustomerBookingsPageInfo {
  hasNextPage: boolean
  endCursor: string | null
}

export interface MyBookingsData {
  myBookings: {
    totalCount: number
    nodes: CustomerBooking[]
    pageInfo: CustomerBookingsPageInfo
  }
}

export interface MyBookingsVariables {
  status?: string
  upcoming?: boolean
  first?: number
  after?: string | null
}

export interface CustomerPropertyPreference {
  id: string
  specialInstructions: string | null
  hasPets: boolean
  petDetails: string | null
  suppliesProvided: boolean
  serviceType: {
    id: string
    name: string
    nameEl: string
    translationKey: string
  }
}

export interface CustomerProperty {
  id: string
  label: string
  propertyType: string
  addressLine1: string
  addressLine2: string | null
  city: string
  postalCode: string
  region: string | null
  country: string
  floor: number | null
  hasElevator: boolean
  sizeSqm: number | null
  bedrooms: number | null
  bathrooms: number | null
  accessInstructions: string | null
  notes: string | null
  isDefault: boolean
  preferences: CustomerPropertyPreference[]
}

export interface MyPropertiesData {
  myProperties: CustomerProperty[]
}

export interface CreatePropertyInput {
  label: string
  propertyType: string
  addressLine1: string
  addressLine2?: string
  city: string
  postalCode: string
  region?: string
  country: string
  floor?: number
  hasElevator?: boolean
  sizeSqm?: number
  bedrooms?: number
  bathrooms?: number
  accessInstructions?: string
  notes?: string
  isDefault?: boolean
}

export interface CreatePropertyData {
  createProperty: {
    property: CustomerProperty | null
    errors: string[]
  }
}

export interface CreatePropertyVariables {
  input: CreatePropertyInput
}

export interface UpdatePropertyInput extends CreatePropertyInput {
  id: string
  active?: boolean
}

export interface UpdatePropertyData {
  updateProperty: {
    property: CustomerProperty | null
    errors: string[]
  }
}

export interface UpdatePropertyVariables {
  input: UpdatePropertyInput
}

export interface DeletePropertyData {
  deleteProperty: {
    success: boolean
    errors: string[]
  }
}

export interface DeletePropertyVariables {
  id: string
}

export interface CancelBookingData {
  cancelBooking: {
    booking: CustomerBooking | null
    errors: string[]
  }
}

export interface CancelBookingVariables {
  bookingId: string
  reason?: string
}
